import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import CustomDropdown from '../../components/controls/CustomDropdown';
import {
    collectVolumeKeys,
    formatChapterLabel,
    formatVolumeLabel,
    getChapterId,
    getChapterNumber,
    getChapterTitle,
    getChapterViews,
    getVolumeKey,
    hasValue,
    NO_VOLUME,
} from './mangaChapters';

const PAGE_SIZE = 50;
// Long series open on one volume instead of the whole list.
const VOLUME_FIRST_THRESHOLD = 60;
// Up to this many volumes fit as season-style tabs; more go into a dropdown.
const MAX_VOLUME_TABS = 8;

const chapterNumberText = (chapter) => {
    const number = getChapterNumber(chapter);
    return hasValue(number) ? String(number) : '';
};

/**
 * Chapter list in the anime season style: number search, volume tabs (or a dropdown for long series),
 * newest-first toggle and a list shown 50 chapters at a time.
 */
const MangaChapterNavigator = ({ chapters, slug, currentChapterId, onSelect, variant, emptyText }) => {
    const navigate = useNavigate();
    const volumeKeys = useMemo(() => collectVolumeKeys(chapters), [chapters]);
    const hasVolumes = volumeKeys.some((key) => key !== NO_VOLUME);
    const currentChapter = useMemo(
        () => chapters.find((chapter) => String(getChapterId(chapter)) === String(currentChapterId)) || null,
        [chapters, currentChapterId]
    );
    const initialVolume = hasVolumes && chapters.length > VOLUME_FIRST_THRESHOLD
        ? (currentChapter ? getVolumeKey(currentChapter) : volumeKeys[0])
        : 'all';

    const [volume, setVolume] = useState(initialVolume);
    const [numberQuery, setNumberQuery] = useState('');
    const [newestFirst, setNewestFirst] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const volumeTouched = useRef(false);
    const listRef = useRef(null);

    // Follow the current chapter until the reader picks a volume by hand.
    useEffect(() => {
        if (!volumeTouched.current) {
            setVolume(initialVolume);
        }
    }, [initialVolume]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [volume, numberQuery, newestFirst]);

    const query = numberQuery.trim().replace(',', '.');
    const filtered = useMemo(() => {
        let list = chapters;
        if (query) {
            list = chapters
                .filter((chapter) => chapterNumberText(chapter).startsWith(query))
                .sort((a, b) => (chapterNumberText(b) === query) - (chapterNumberText(a) === query));
            return list;
        }
        if (volume !== 'all') {
            list = chapters.filter((chapter) => getVolumeKey(chapter) === volume);
        }
        return newestFirst ? [...list].reverse() : list;
    }, [chapters, query, volume, newestFirst]);

    const exactMatch = query ? filtered.find((chapter) => chapterNumberText(chapter) === query) : null;
    const visible = filtered.slice(0, visibleCount);
    const remaining = filtered.length - visible.length;
    const showGroupLabels = hasVolumes && volume === 'all' && !query;

    useEffect(() => {
        if (variant !== 'sidebar' || !listRef.current) {
            return;
        }
        const current = listRef.current.querySelector('.manga-chapter-card.selected');
        if (current) {
            listRef.current.scrollTop = Math.max(current.offsetTop - listRef.current.offsetTop - 80, 0);
        }
    }, [variant, currentChapterId, volume]);

    if (chapters.length === 0) {
        return <div className="manga-empty">{emptyText || 'Глав пока нет.'}</div>;
    }

    const openChapter = (chapter) => {
        const chapterId = getChapterId(chapter);
        if (!chapterId) {
            return;
        }
        if (onSelect) {
            onSelect(chapter);
        } else {
            navigate(`/manga/${slug}/read?chapterId=${chapterId}`);
        }
    };

    const selectVolume = (key) => {
        volumeTouched.current = true;
        setNumberQuery('');
        setVolume(key);
    };

    const volumeOptions = [
        { value: 'all', label: 'Все тома' },
        ...volumeKeys.map((key) => ({ value: key, label: formatVolumeLabel(key) })),
    ];

    const rows = [];
    let lastGroup = null;
    visible.forEach((chapter) => {
        const chapterId = getChapterId(chapter);
        const group = getVolumeKey(chapter);
        if (showGroupLabels && group !== lastGroup) {
            rows.push(<div key={`group-${group}`} className="manga-volume-title">{formatVolumeLabel(group)}</div>);
            lastGroup = group;
        }
        const isSelected = hasValue(currentChapterId) && String(chapterId) === String(currentChapterId);
        const name = getChapterTitle(chapter);
        const views = getChapterViews(chapter);
        rows.push(
            <div
                key={chapterId || name}
                className={`manga-chapter-card${isSelected ? ' selected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => openChapter(chapter)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        openChapter(chapter);
                    }
                }}
            >
                <div>
                    <div className="manga-chapter-title">
                        {formatChapterLabel(chapter)} {name ? `· ${name}` : ''}
                    </div>
                    {isSelected ? (
                        <div className="manga-chapter-meta">{variant === 'sidebar' ? 'Вы читаете эту главу' : 'Вы остановились здесь'}</div>
                    ) : null}
                    {!isSelected && hasValue(views) && variant !== 'sidebar' ? (
                        <div className="manga-chapter-meta">Просмотров: {views}</div>
                    ) : null}
                </div>
                <button
                    type="button"
                    className="ui-btn ui-btn--secondary ui-btn--sm"
                    onClick={(event) => {
                        event.stopPropagation();
                        openChapter(chapter);
                    }}
                    disabled={!chapterId}
                >
                    {isSelected && variant !== 'sidebar' ? 'Продолжить' : 'Читать'}
                </button>
            </div>
        );
    });

    return (
        <div className={`season-component manga-chapters-nav manga-chapters-nav--${variant}`}>
            <div className={`season-controls${hasVolumes && volumeKeys.length > MAX_VOLUME_TABS ? ' has-dropdown' : ''}`}>
                <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    min="0"
                    className="series-find-input"
                    placeholder="Номер"
                    aria-label="Номер главы"
                    value={numberQuery}
                    onChange={(event) => setNumberQuery(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && exactMatch) {
                            openChapter(exactMatch);
                        }
                    }}
                />
                {hasVolumes && volumeKeys.length <= MAX_VOLUME_TABS ? (
                    <>
                        <button
                            type="button"
                            className={volume === 'all' && !query ? 'tab-button selected' : 'tab-button'}
                            onClick={() => selectVolume('all')}
                        >
                            Все
                        </button>
                        {volumeKeys.map((key) => (
                            <button
                                key={key}
                                type="button"
                                className={volume === key && !query ? 'tab-button selected' : 'tab-button'}
                                onClick={() => selectVolume(key)}
                            >
                                {formatVolumeLabel(key)}
                            </button>
                        ))}
                    </>
                ) : null}
                {hasVolumes && volumeKeys.length > MAX_VOLUME_TABS ? (
                    <CustomDropdown
                        placeholder="Том"
                        title="Выберите том"
                        value={volume}
                        onChange={selectVolume}
                        options={volumeOptions}
                    />
                ) : null}
                <button
                    type="button"
                    className="tab-button"
                    onClick={() => setNewestFirst((value) => !value)}
                    disabled={Boolean(query)}
                >
                    {newestFirst ? 'Сначала новые ↓' : 'По порядку ↑'}
                </button>
            </div>

            {query && filtered.length === 0 ? (
                <div className="manga-empty">Главы с номером {query} нет.</div>
            ) : null}

            <div className="manga-chapter-list" ref={listRef} key={`${volume}|${query}|${newestFirst}`}>
                {rows}
            </div>

            {remaining > 0 ? (
                <button
                    type="button"
                    className="ui-btn ui-btn--secondary ui-btn--block manga-chapter-more"
                    onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                >
                    Показать ещё {Math.min(remaining, PAGE_SIZE)} из {remaining}
                </button>
            ) : null}
        </div>
    );
};

MangaChapterNavigator.propTypes = {
    chapters: PropTypes.arrayOf(PropTypes.object).isRequired,
    slug: PropTypes.string,
    currentChapterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelect: PropTypes.func,
    variant: PropTypes.oneOf(['page', 'sidebar']),
    emptyText: PropTypes.string,
};

MangaChapterNavigator.defaultProps = {
    slug: '',
    currentChapterId: null,
    onSelect: null,
    variant: 'page',
    emptyText: null,
};

export default MangaChapterNavigator;
